import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  getImportantDates,
  createImportantDate,
  updateImportantDate,
  deleteImportantDate,
  bulkDeleteImportantDates,
} from '../api/importantDateService';

const ImportantDateContext = createContext();

export const useImportantDates = () => {
  const context = useContext(ImportantDateContext);
  if (!context) {
    throw new Error('useImportantDates must be used within ImportantDateProvider');
  }
  return context;
};

export const ImportantDateProvider = ({ children }) => {
  const [importantDates, setImportantDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch all important dates
  const fetchImportantDates = async (params = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await getImportantDates(params);
      if (response.status === 'success') {
        setImportantDates(response.data.importantDates || []);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch important dates');
      console.error('Error fetching important dates:', err);
    } finally {
      setLoading(false);
    }
  };

  // Add new important date
  const addImportantDate = async (data) => {
    try {
      setLoading(true);
      setError(null);
      const response = await createImportantDate(data);
      if (response.status === 'success') {
        setImportantDates((prev) => [...prev, response.data.importantDate]);
        return response.data.importantDate;
      }
    } catch (err) {
      setError(err.message || 'Failed to create important date');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update existing important date
  const updateExistingImportantDate = async (id, data) => {
    try {
      setLoading(true);
      setError(null);
      const response = await updateImportantDate(id, data);
      if (response.status === 'success') {
        setImportantDates((prev) =>
          prev.map((date) => (date._id === id ? response.data.importantDate : date))
        );
        return response.data.importantDate;
      }
    } catch (err) {
      setError(err.message || 'Failed to update important date');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Delete important date
  const removeImportantDate = async (id) => {
    try {
      setLoading(true);
      setError(null);
      await deleteImportantDate(id);
      setImportantDates((prev) => prev.filter((date) => date._id !== id));
    } catch (err) {
      setError(err.message || 'Failed to delete important date');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Bulk delete important dates
  const bulkRemoveImportantDates = async (ids) => {
    try {
      setLoading(true);
      setError(null);
      await bulkDeleteImportantDates(ids);
      setImportantDates((prev) => prev.filter((date) => !ids.includes(date._id)));
    } catch (err) {
      setError(err.message || 'Failed to delete important dates');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Load important dates on mount
  useEffect(() => {
    let isMounted = true;

    const loadDates = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getImportantDates({});
        if (isMounted && response.status === 'success') {
          setImportantDates(response.data.importantDates || []);
        }
      } catch (err) {
        if (import.meta.env.DEV) {
          console.error('Error fetching important dates:', err);
        }
        if (isMounted) {
          setError(err.message || 'Failed to fetch important dates');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadDates();

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, []);

  const value = {
    importantDates,
    loading,
    error,
    fetchImportantDates,
    addImportantDate,
    updateImportantDate: updateExistingImportantDate,
    deleteImportantDate: removeImportantDate,
    bulkDeleteImportantDates: bulkRemoveImportantDates,
  };

  return (
    <ImportantDateContext.Provider value={value}>
      {children}
    </ImportantDateContext.Provider>
  );
};
